import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { presentations as presentationsApi } from "apis/callables";
import { GetSlidesMetadataResponse } from "apis/types";
import getObjectHash from "common/utils/getObjectHash";
import { IWorkspaceResourceFolder, WorkspaceResourceType } from "common/interfaces";

import PresentationTemplatesPaneController, { PresentationTemplatesPaneControllerState } from "js/controllers/PresentationTemplatesPaneController";
import { t } from "js/i18n";
import { NoMatchNotice, NoTeamTemplateNotice, SharedSlideFolderNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { PresentationTemplateItem, PresentationTemplateItemSkeleton } from "js/react/views/SharedResources/Components/PresentationTemplateItem";
import { mouseDragItems } from "js/react/views/SharedResources/utils/mouseDragHelper";
import { removeResourcesFromFolder } from "js/react/views/SharedResources/utils/removeResourcesFromFolder";

export type PresentationTemplateDragItem = {
    id: string;
    resourceId: string;
    resourceType: WorkspaceResourceType;
};

export interface PresentationTemplateListProps {
    editable: boolean;
    allowDragging?: boolean;
    selectedFolderId?: string | null;
    resourceFolders?: IWorkspaceResourceFolder[];
    onSelect?: (presentationId: string | null, isDoubleClick?: boolean) => void;
    onStartDragItems?: (items: PresentationTemplateDragItem[]) => void;
    onDropDragItems?: () => void;
    onNavigateToFolder?: (filter: { folderId: string; subFolderId?: string }) => void;
}

const ListContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    padding: 0;
    flex: 1;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
`;

const Container = styled.div`
    width: 100%;
    min-width: 0;
`;

function PresentationTemplateList({
    editable,
    allowDragging = false,
    selectedFolderId = null,
    resourceFolders = [],
    onSelect,
    onStartDragItems,
    onDropDragItems,
    onNavigateToFolder,
    data,
    filters,
    search,
    fetching,
}: PresentationTemplateListProps & PresentationTemplatesPaneControllerState) {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const presentationsSlidesMetadataRef = useRef<Record<string, Promise<GetSlidesMetadataResponse>>>({});
    const listRef = useRef<HTMLDivElement>(null);
    const startSelectionIndexRef = useRef(0);
    const dragStartedRef = useRef(false);
    const itemWasSelectedOnMouseDownRef = useRef(false);

    const handleGetPresentationSlidesMetadata = useCallback(async (presentationId: string) => {
        if (!presentationsSlidesMetadataRef.current[presentationId]) {
            presentationsSlidesMetadataRef.current[presentationId] = presentationsApi.getSlidesMetadata({ id: presentationId });
        }
        return presentationsSlidesMetadataRef.current[presentationId];
    }, []);

    const handleSelect = useCallback((presentationId: string | null, isDoubleClick?: boolean) => {
        if (!editable) {
            setSelectedIds(presentationId ? [presentationId] : []);
        }
        onSelect?.(presentationId, isDoubleClick);
    }, [editable, onSelect]);

    const toDragItem = useCallback((record: PresentationTemplatesPaneControllerState["data"][number]): PresentationTemplateDragItem => ({
        id: record.document.id,
        resourceId: record.resource.resourceId,
        resourceType: record.resource.resourceType,
    }), []);

    const toDragItems = useCallback((ids: string[]) => {
        return ids.flatMap(id => {
            const record = data.find(item => item.document.id === id);
            return record ? [toDragItem(record)] : [];
        });
    }, [data, toDragItem]);

    const handleItemMouseDown = useCallback((
        record: PresentationTemplatesPaneControllerState["data"][number],
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (!allowDragging) {
            return;
        }

        event.stopPropagation();
        const presentationId = record.document.id;
        let nextSelection = [...selectedIds];
        itemWasSelectedOnMouseDownRef.current = nextSelection.includes(presentationId);

        if (event.metaKey || event.ctrlKey) {
            if (nextSelection.includes(presentationId)) {
                nextSelection = nextSelection.filter(id => id !== presentationId);
            } else {
                nextSelection = [...nextSelection, presentationId];
            }
        } else if (event.shiftKey && nextSelection.length) {
            const startIndex = startSelectionIndexRef.current;
            const endIndex = data.findIndex(item => item.document.id === presentationId);

            if (endIndex > startIndex) {
                for (let i = startIndex + 1; i <= endIndex; i++) {
                    const id = data[i].document.id;
                    if (!nextSelection.includes(id)) {
                        nextSelection.push(id);
                    }
                }
            } else {
                for (let i = startIndex; i >= endIndex; i--) {
                    const id = data[i].document.id;
                    if (!nextSelection.includes(id)) {
                        nextSelection.push(id);
                    }
                }
            }
        } else {
            if (!nextSelection.includes(presentationId)) {
                nextSelection = [presentationId];
                startSelectionIndexRef.current = data.findIndex(item => item.document.id === presentationId);
            }

            const dragSelection = toDragItems(nextSelection);
            const mousedItem = toDragItem(record);
            dragStartedRef.current = false;

            mouseDragItems({
                event,
                container: listRef.current,
                mousedItem,
                selection: dragSelection,
                onDidStartDrag: () => {
                    dragStartedRef.current = true;
                },
                onStartDragItems: () => onStartDragItems?.(dragSelection),
                onDropDragItems: () => onDropDragItems?.(),
            });
        }

        setSelectedIds(nextSelection);
    }, [allowDragging, data, onDropDragItems, onStartDragItems, selectedIds, toDragItem, toDragItems]);

    const handleItemMouseUp = useCallback((
        record: PresentationTemplatesPaneControllerState["data"][number],
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (!allowDragging) {
            return;
        }

        if (event.shiftKey || event.metaKey || event.ctrlKey) {
            return;
        }

        const presentationId = record.document.id;
        const didDrag = dragStartedRef.current;
        const itemWasSelectedOnMouseDown = itemWasSelectedOnMouseDownRef.current;

        event.preventDefault();

        setSelectedIds(current => {
            const isDeselect = itemWasSelectedOnMouseDown &&
                !didDrag &&
                current.length === 1 &&
                current[0] === presentationId;

            return isDeselect ? [] : [presentationId];
        });
    }, [allowDragging]);

    const handleListMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!allowDragging) {
            return;
        }

        if ((event.target as HTMLElement).closest("[data-item-id]")) {
            return;
        }

        setSelectedIds([]);
    }, [allowDragging]);

    const handleItemClick = useCallback((presentationId: string | null, isDoubleClick?: boolean) => {
        setSelectedIds(presentationId ? [presentationId] : []);
        onSelect?.(presentationId, isDoubleClick);
    }, [onSelect]);

    const handleItemMenuClick = useCallback((presentationId: string) => {
        setSelectedIds([presentationId]);
    }, []);

    const handleRemoveFromFolder = useCallback(async (presentationId: string) => {
        if (!selectedFolderId) {
            return;
        }

        const ids = selectedIds.includes(presentationId) && selectedIds.length > 0
            ? selectedIds
            : [presentationId];
        const resources = toDragItems(ids).map(({ resourceId, resourceType }) => ({
            resourceId,
            resourceType,
        }));

        await removeResourcesFromFolder({
            folderId: selectedFolderId,
            resources,
            resourceLabel: ids.length === 1 ? t("template") : t("templates"),
        });
    }, [selectedFolderId, selectedIds, toDragItems]);

    const handleVisibleChange = useCallback((presentationId: string, isVisible: boolean) => {
        if (!isVisible || !PresentationTemplatesPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(record => record.document.id === presentationId);
        if (index === data.length - 1) {
            void PresentationTemplatesPaneController.loadMore();
        }
    }, [data]);

    useEffect(() => {
        setSelectedIds(current => current.filter(id => data.some(record => record.document.id === id)));
    }, [getObjectHash(data)]);

    const hasFilter = filters.some(filter =>
        (filter.field !== "resource.metadata.isPublished" && filter.field !== "resource.folderIds")) || !!search.query;
    const hasNoMatch = data.length === 0 && hasFilter;
    const hasNoData = data.length === 0 && !hasFilter;
    const isLoadingMore = fetching && data.length > 0;
    const isLoadingInitial = fetching && data.length === 0;

    if (isLoadingInitial || !PresentationTemplatesPaneController.initialized) {
        return <Spinner />;
    }

    if (hasNoMatch) {
        return <NoMatchNotice />;
    }

    if (hasNoData) {
        if (selectedFolderId) {
            return (<SharedSlideFolderNotice hideMessage={!editable} />);
        }

        return (
            <ListContainer>
                <NoTeamTemplateNotice />
            </ListContainer>
        );
    }

    return (
        <ListContainer>
            <Container ref={listRef} onMouseDown={handleListMouseDown}>
                {data.map(record => (
                    <PresentationTemplateItem
                        key={record.document.id}
                        record={record}
                        editable={editable}
                        onSelect={allowDragging ? handleSelect : handleItemClick}
                        onMouseDown={allowDragging ? event => handleItemMouseDown(record, event) : undefined}
                        onMouseUp={allowDragging ? event => handleItemMouseUp(record, event) : undefined}
                        onMenuClick={allowDragging ? handleItemMenuClick : undefined}
                        selected={selectedIds.includes(record.document.id)}
                        getPresentationSlidesMetadata={handleGetPresentationSlidesMetadata}
                        onVisibleChange={isVisible => handleVisibleChange(record.document.id, isVisible)}
                        selectedFolderId={selectedFolderId}
                        onRemoveFromFolder={selectedFolderId ? handleRemoveFromFolder : undefined}
                        resourceFolders={resourceFolders}
                        onNavigateToFolder={onNavigateToFolder}
                    />
                ))}
                {isLoadingMore && <PresentationTemplateItemSkeleton />}
            </Container>
        </ListContainer>
    );
}

export default PresentationTemplatesPaneController.withState(PresentationTemplateList);
